import NavMenu from "@/components/NavMenu";
import Clock from "@/components/shared/Clock";
import CopyRightFooter from "@/components/shared/CopyRightFooter";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { userNamespace } from "@/store/modules/user";
import Chart from "chart.js";
import _ from "lodash";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

const PERF_POLLING_INTERVAL = 10000;

@Component({
  components: { Clock, CopyRightFooter, NavMenu }
})
export default class Status extends Vue {
  active = "Status";
  performanceChart!: Chart;
  isBuilt = false;

  @Getter("darkMode", navigationNamespace)
  dark!: boolean;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("user", userNamespace)
  user!: any;

  @Getter("system", userNamespace)
  systemInfo!: Record<string, any>;

  @Action("performanceResults", userNamespace)
  performanceResults!: Function;

  @Action("commentsRequest", userNamespace)
  commentsRequest!: Function;

  @Action("commentDataRequest", userNamespace)
  commentDataRequest!: Function;

  @Action("errorSummary", userNamespace)
  errorSummary!: Function;

  interval: any = null;
  lastTimestamp: number = 0;
  firstTimestamp: number = 0;

  comments: any[] = [];

  commentData: Record<number, string> = [];

  commentDialog = false;
  commentDialogID = 0;
  commentDialogText = "";

  errorSummaryText: string = "";

  performanceData: any[] = [];
  movingAverage: any[] = [];
  ptCount: any[] = [];

  greenFill = "rgba(0, 255, 0, 0.5)";
  greenBorder = "rgba(0, 255, 0, 1.0)";

  orangeFill = "rgba(249, 159, 28, 0.5)";
  orangeBorder = "rgba(249, 159, 28, 1.0)";

  lightBlueFill = "rgba(0, 158, 219, 1.0)";
  lightBlueBorder = "rgba(0, 158, 219, 1.0)";

  darkBlueFill = "rgba(66, 138, 248, 0.5)";
  darkBlueBorder = "rgba(66, 138, 248, 1.0)";

  get performanceFill() {
    return this.dark ? this.greenFill : this.darkBlueFill;
  }
  get performanceBorder() {
    return this.dark ? this.greenBorder : this.darkBlueBorder;
  }
  get averageFill() {
    return this.dark ? this.orangeFill : this.lightBlueFill;
  }
  get averageBorder() {
    return this.dark ? this.orangeBorder : this.lightBlueBorder;
  }

  get ptCountColor() {
    return this.dark ? "#FFF" : "#000";
  }

  get memColor() {
    return this.dark ? "#FF7BAC" : "#FF7BAC";
  }

  maxPerformanceHeight = 12;

  get chartConfig() {
    return {
      type: "line",
      data: {
        datasets: [
          {
            label: "Cycle Time",
            type: "line",
            yAxisID: "CycleTime",
            fill: false,
            borderWidth: 1,
            pointRadius: 0,
            pointBackgroundColor: this.performanceFill,
            pointBorderColor: this.performanceBorder,
            showLine: true,
            spanGaps: false,
            data: [],
            backgroundColor: "black"
          },
          {
            label: "Moving Average",
            type: "line",
            yAxisID: "MovingAvg",
            fill: false,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: this.averageFill,
            pointBorderColor: this.averageBorder,
            showLine: true,
            spanGaps: false,
            data: [],
            backgroundColor: "black"
          },
          {
            label: "Patients",
            type: "line",
            yAxisID: "PtCount",
            fill: false,
            borderWidth: 2,
            pointRadius: 1,
            pointBackgroundColor: this.ptCountColor,
            pointBorderColor: this.ptCountColor,
            showLine: false,
            spanGaps: false,
            data: [],
            backgroundColor: "black"
          },
          {
            label: "Memory",
            type: "line",
            yAxisID: "Mem",
            fill: false,
            borderWidth: 2,
            pointRadius: 1,
            pointBackgroundColor: this.memColor,
            pointBorderColor: this.memColor,
            pointStyle: "triangle",
            showLine: false,
            spanGaps: false,
            data: [],
            backgroundColor: "black"
          }
        ]
      },
      options: {
        scales: {
          xAxes: [
            {
              gridLines: { color: "#000000" },
              ticks: {
                display: true,
                fontColor: this.dark ? "white" : "black",
                maxRotation: 90,
                minRotation: 0
              },
              time: {
                unit: this.performanceData.length < 60 ? "minute" : "hour"
              },
              type: "time"
            }
          ],
          yAxes: [
            {
              id: "CycleTime",
              position: "left",
              ticks: {
                display: true,
                mirror: true,
                fontColor: this.performanceBorder,
                max: this.maxPerformanceHeight,
                beginAtZero: true
              }
            },
            {
              id: "MovingAvg",
              position: "left",
              ticks: {
                display: false,
                mirror: false,
                fontColor: this.averageBorder,
                max: this.maxPerformanceHeight,
                beginAtZero: true
              }
            },
            {
              id: "PtCount",
              position: "right",
              ticks: {
                display: true,
                mirror: true,
                fontColor: this.ptCountColor,
                beginAtZero: true
              }
            },
            {
              id: "Mem",
              position: "right",
              ticks: {
                display: true,
                mirror: true,
                fontColor: this.memColor,
                beginAtZero: true
              }
            }
          ]
        },
        legend: {
          display: true,
          labels: {
            fontStyle: "bold",
            fontColor: this.dark ? "white" : "black"
          }
        },
        elements: { line: { tension: 0.25 } },
        animation: { duration: 0.5 },
        // hover: { mode: undefined },
        tooltips: {
          enabled: true,
          intersect: false
        },
        // maintainAspectRatio: false,
        responsive: true
      }
    };
  }

  @Watch("dark")
  @Watch("performanceData")
  updateChartOptions() {
    const ops = this.chartConfig.options as Chart.ChartOptions;
    this.performanceChart.options = ops;
    this.performanceChart.update();
  }

  async mounted() {
    this.buildGraph();
    this.setLabel();
    this.updatePerformanceData();
    if (!this.interval) {
      this.interval = setInterval(() => {
        this.updatePerformanceData();
      }, PERF_POLLING_INTERVAL);
    }
    this.updateComments();
    this.updateErrorSummary();
  }

  setLabel() {
    const cycles = _.get(this, "performanceChart.data.datasets[0]");
    if (cycles) {
      cycles.label = "Cycle Time [s]";
      cycles.borderColor = this.performanceBorder;
      cycles.backgroundColor = this.performanceFill;
    }

    const avg = _.get(this, "performanceChart.data.datasets[1]");
    if (avg) {
      avg.label = "Moving Average [s]";
      avg.borderColor = this.averageBorder;
      avg.backgroundColor = this.averageFill;
    }

    const pts = _.get(this, "performanceChart.data.datasets[2]");
    if (pts) {
      //pts.label = "Moving Average [s]";
      pts.borderColor = this.ptCountColor;
      pts.backgroundColor = this.ptCountColor;
    }

    const mem = _.get(this, "performanceChart.data.datasets[3]");
    if (mem) {
      //pts.label = "Moving Average [s]";
      mem.borderColor = this.memColor;
      mem.backgroundColor = this.memColor;
    }
  }

  get systemName() {
    const sytm: string = _.get(this, "systemInfo.system", "");
    return sytm.replace("AW.", "");
  }

  makePerformanceEntry(d: any) {
    return {
      x: d.ts.split(".")[0].replace("T", " "),
      y: d.ms != -1 ? d.ms / 1000.0 : null
    };
  }

  pushPerformanceGraph(local: any[], data: any[], ds: any) {
    let toAdd: any[] = [];
    let newData: any[] = [];
    if (data !== null) {
      if (local.length === 0) {
        Array.prototype.push.apply(local, data);
        Array.prototype.push.apply(newData, data);
      } else {
        const last = local[local.length - 1].ts;
        newData = data.filter((f, i) => f.ts > last);
        if (newData.length > 0) {
          Array.prototype.push.apply(local, newData);
        }
      }
      if (ds && ds.data) {
        toAdd = newData.map(d => this.makePerformanceEntry(d));
        if (toAdd.length > 0) {
          Array.prototype.push.apply(ds.data, toAdd);
        }
      }
    }
  }

  async updatePerformanceData() {
    const last =
      this.performanceData.length > 0
        ? this.performanceData[this.performanceData.length - 1].ts
        : null;

    const result: any =
      last == null
        ? await this.performanceResults()
        : await this.performanceResults(last);

    const ds = _.get(this, "performanceChart.config.data.datasets");

    // update performance data
    const perf: any[] = result.perf;
    if (perf.length > 0) {
      this.pushPerformanceGraph(this.performanceData, perf, ds[0]);
    }

    // update moving average
    const avg: any[] = result.avg;
    if (avg.length > 0) {
      this.pushPerformanceGraph(this.movingAverage, avg, ds[1]);
    }

    // set the max height of the performance
    let maxSeconds = Math.max.apply(
      Math,
      this.performanceData.map(function(o) {
        return o.ms / 1000.0;
      })
    );
    maxSeconds = Math.round(maxSeconds / 10) * 10;

    if (maxSeconds > this.maxPerformanceHeight)
      this.maxPerformanceHeight = maxSeconds;

    // pt count
    const data: any[] = [];
    for (let key in result.pts) {
      let value = result.pts[key];
      data.push({ x: key.split(".")[0].replace("T", " "), y: value });
    }
    if (data.length > 1 || ds[2].data.length === 0) {
      Array.prototype.push.apply(ds[2].data, data);
    } else if (data.length > 0) {
      const lastEntry = ds[2].data[ds[2].data.length - 1];
      if (
        lastEntry != undefined &&
        lastEntry.x != undefined &&
        lastEntry.x != data[0].x &&
        lastEntry.y != data[0].y
      ) {
        Array.prototype.push.apply(ds[2].data, data);
      }
    }

    // pt count
    const mem: any[] = [];
    for (let key in result.mem) {
      let value = result.mem[key];
      mem.push({ x: key.split(".")[0].replace("T", " "), y: value / 1000000 });
    }
    if (mem.length > 1 || ds[2].data.length === 0) {
      Array.prototype.push.apply(ds[3].data, mem);
    } else if (mem.length > 0) {
      const lastEntry = ds[2].data[ds[3].data.length - 1];
      if (
        lastEntry != undefined &&
        lastEntry.x != undefined &&
        lastEntry.x != mem[0].x &&
        lastEntry.y != mem[0].y
      ) {
        Array.prototype.push.apply(ds[3].data, mem);
      }
    }
    this.updateChartOptions();
  }

  get isMobileThin() {
    return (
      this.$vuetify.breakpoint.smAndDown && this.$vuetify.breakpoint.width < 600
    );
  }

  buildGraph() {
    if (!this.isBuilt) {
      const canvas = this.$refs.canvas as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        this.performanceChart = new Chart(
          ctx,
          this.chartConfig as Chart.ChartConfiguration
        );
      }
      this.isBuilt = true;
    }
  }
  get showName() {
    return this.$vuetify.breakpoint.width > 555;
  }

  get userFullName() {
    return this.user.name;
  }

  back() {
    this.$router.go(-1);
  }
  beforeDestroy() {
    this.stopPolling();
  }
  private stopPolling() {
    clearInterval(this.interval);
    this.interval = null;
  }

  async updateComments() {
    this.comments = await this.commentsRequest();
  }

  async loadCommentData(logID: number): Promise<string> {
    if (this.commentData[logID] == null) {
      const data = await this.commentDataRequest(logID);
      this.commentData[logID] = data;
    }
    return Promise.resolve(this.commentData[logID]);
  }

  async getCommentData(logID: number) {
    const comment = await this.loadCommentData(logID);
    this.commentDialogID = logID;
    this.commentDialogText = comment; //JSON.stringify(comment, null, 2);
    this.commentDialog = true;
  }

  async copyCommentData(logID: number) {
    const comment = await this.loadCommentData(logID);
    this.copyMessage(JSON.stringify(comment, null, 2));
    alert("Copied data for logID = " + logID.toString() + " to clipboard");
  }

  copyMessage(val: string) {
    const selBox = document.createElement("textarea");
    selBox.style.position = "fixed";
    selBox.style.left = "0";
    selBox.style.top = "0";
    selBox.style.opacity = "0";
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand("copy");
    document.body.removeChild(selBox);
  }

  async updateErrorSummary() {
    this.errorSummaryText = await this.errorSummary();
  }

  get errorSummaryDisplay() {
    return this.errorSummaryText !== "[count=0]" ? this.errorSummaryText : "";
  }

  syntaxHighlight(json: string) {
    if (typeof json != "string") {
      json = JSON.stringify(json, undefined, 2);
    }
    json = json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      function(match) {
        var cls = "number";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "key";
          } else {
            cls = "string";
          }
        } else if (/true|false/.test(match)) {
          cls = "boolean";
        } else if (/null/.test(match)) {
          cls = "null";
        }
        return '<span class="' + cls + '">' + match + "</span>";
      }
    );
  }
}
